
import {
  defineComponent, ref, watchEffect, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import EditorModal from '@/components/modals/EditorModal.vue';
import Loader from '@/components/layout/Loader.vue';
import {
  fetchCustomSchemaDefinition,
  fetchAllOverrides,
  fetchCustomSchemas,
} from '@/composables/custom-data/customData';
import { useStore } from '@/store';
import { showToastError, showToastSuccess } from '@/helpers';
import { validateSettingsWithVuelidate } from '@/composables/custom-data/customData.validations';
import { ApiReferenceModal } from '@/views/my-shop-data/ObjectsDataListActions';
import SchemaRelationsEditor from './custom-schema-editor/SchemaRelationsEditor.vue';
import SchemaFieldsEditor from './custom-schema-editor/SchemaFieldsEditor.vue';
import SchemaSettingsPanel from './custom-schema-editor/SchemaSettingsPanel.vue';

export default defineComponent({
  name: 'CustomSchemaEditorModal',
  components: {
    EditorModal,
    SchemaSettingsPanel,
    SchemaFieldsEditor,
    SchemaRelationsEditor,
    Loader,
    ApiReferenceModal,
  },

  props: {
    idCustomData: {
      type: Number,
      required: false,
      default: undefined,
    },
  },

  emits: ['close'],
  setup(props, { emit }) {
    const store = useStore();
    const isLoading = ref(false);
    const { t } = useI18n();
    const settingPanelValidationErrors = ref({});
    const showSettingsSidebar = ref(false);
    const apiReferenceRef = ref<{ open: (payload: any) => void } | null>(null);
    const isInitialized = computed(() => store.getters['customObject/isInitialized']);
    const settings = computed(() => store.getters['customObject/getSettings']);

    // Toggle settings sidebar
    const toggleSettingsSidebar = () => {
      showSettingsSidebar.value = !showSettingsSidebar.value;
    };

    // pre-fill if edition (API loading)
    watchEffect(async () => {
      // Show loader when loading data for edition mode
      isLoading.value = true;
      try {
        const customObjects = await fetchCustomSchemas();
        const allOverrides = await fetchAllOverrides();
        let customDataDefinitionEntity = null;
        // if idCustomData is provided, fetch the custom data definition from the API
        if (props.idCustomData) {
          const fetchCustomDataDefinitionEntity = await fetchCustomSchemaDefinition(props.idCustomData);
          if (!fetchCustomDataDefinitionEntity.success) {
            showToastError(t('customData.messages.fetchObjectError'));
            emit('close');
            return;
          }
          customDataDefinitionEntity = fetchCustomDataDefinitionEntity.data;
        }
        // initialize the custom object
        store.dispatch('customObject/initializeCustomObject', { customDataDefinitionEntity, customObjects, allOverrides });
      } finally {
        // Hide loader once data is loaded
        isLoading.value = false;
      }
    });

    const contentHeaderStartToolbar = computed(() => ([
      {
        icon: 'far fa-fw fa-cog',
        label: t('customData.toolbar.label.settings'),
        tooltip: t('customData.toolbar.tooltip.settings'),
        class: () => (showSettingsSidebar.value ? 'active' : ''),
        command: () => toggleSettingsSidebar(),
      },
    ]));

    const storeFields = computed(() => store.getters['customObject/getFields'] || []);

    const contentHeaderEndToolbar = computed(() => [
      {
        icon: 'fas fa-fw fa-code',
        label: t('myShopData.actions.apiReference'),
        tooltip: t('myShopData.actions.apiReference'),
        command: () => {
          if (apiReferenceRef.value?.open) {
            // Passe directement l'état courant du store (fonctionne en création et en édition)
            apiReferenceRef.value.open({
              schemaName: settings.value.name || t('customData.editorTitle'),
              definitionId: props.idCustomData,
              fields: storeFields.value.map((f: any) => ({ name: f.name, type: f.type })),
            });
          }
        },
      },
    ]);

    const toolbarHeaderRightButtons = [
      {
        label: t('statsEditor.headerToolbar.save'),
        class: 'p-button-secondary',
        items: [
          {
            label: t('statsEditor.headerToolbar.saveAndLeave'),
            command: async () => {
              isLoading.value = true;
              try {
                const settingsValid = await validateSettingsWithVuelidate(settings.value);
                if (!settingsValid.valid) {
                  // Update validation errors with plain error objects (not reactive Vuelidate internals)
                  settingPanelValidationErrors.value = settingsValid.errors;
                  isLoading.value = false;
                  // Defer sidebar opening to avoid component update conflicts
                  setTimeout(() => {
                    if (!showSettingsSidebar.value) {
                      showSettingsSidebar.value = true;
                    }
                  }, 0);
                  return;
                }
                // Clear validation errors only if valid
                settingPanelValidationErrors.value = {};
                const result = await store.dispatch('customObject/saveCustomObject');
                if (result && result.success) {
                  showToastSuccess(t('customData.messages.saveObjectSuccess'));
                  // Reset the state to avoid old data when reopening the editor
                  store.commit('customObject/resetState');
                  emit('close');
                } else {
                  showToastError(result?.error || t('customData.messages.saveObjectError'));
                }
              } catch (error) {
                showToastError(t('customData.messages.saveObjectError'));
              } finally {
                isLoading.value = false;
              }
            },
          },
          {
            label: t('statsEditor.headerToolbar.leaveWithoutSaving'),
            command: () => {
              // Reset the state to avoid old data when reopening the editor
              store.commit('customObject/resetState');
              emit('close');
            },
          },
        ],
      },
    ];

    return {
      t,
      toolbarHeaderRightButtons,
      contentHeaderStartToolbar,
      contentHeaderEndToolbar,
      apiReferenceRef,
      isLoading,
      isInitialized,
      settings,
      settingPanelValidationErrors,
      showSettingsSidebar,
    };
  },
});
